import axios from "axios";
import {
  CreateApp,
  GlobalAxiosResponse,
  GoogleUser,
  updateApp,
  Theme,
  ModalTaskType,
} from "@/types";
import useCurrentUserStore from "@/store";
import { omit } from "lodash";

// INITAILZE AXIOS
export const api = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const access_token = useCurrentUserStore.getState().dashboardAccessToken;
  const app_name = useCurrentUserStore.getState().appName;

  if (access_token) {
    config.headers.Authorization = `Bearer ${access_token}`;
  }
  if (app_name) {
    config.headers["x-app-name"] = app_name;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status === 403) {
      useCurrentUserStore.setState({});
      useCurrentUserStore.setState({
        dashboardAccessToken: undefined,
        email: undefined,
        userData: undefined,
        currentUser: undefined,
        appId: undefined,
        appSlug: undefined,
        appName: undefined,
        generatedBlog: undefined,
        isOnboardingComplete: false,
        userCreated: false,
        selectedApp: undefined,
        appLoginToken: undefined,
        isBlogGenerating: false,
      });
      window.location.href = "/";
    }
    // reject with error if response status is not 403
    return Promise.reject(error);
  }
);

// Basic Auth
export const register = async (props: {
  email: string;
  contact_number: string;
  avatar?: string;
}): Promise<
  GlobalAxiosResponse<{ email?: string; otp?: string; magic_link?: string }>
> => api.post("/client/auth/register", props);

export const login = async (props: {
  email: string;
}): Promise<GlobalAxiosResponse<{ email: string }>> =>
  api.post("/api/v1/auth/login", props);

// Google Auth
export const googleRegister = async (props: {
  token: string;
}): Promise<GlobalAxiosResponse<GoogleUser>> =>
  api.post("/client/auth/register/google", props);

export const googleLogin = async (props: {
  token: string;
}): Promise<GlobalAxiosResponse<GoogleUser>> =>
  api.post("/api/v1/auth/login/google", props);

// OTP
export const verifyOTP = async (props: {
  email: string;
  otp: string;
}): Promise<GlobalAxiosResponse<GoogleUser>> =>
  api.post("/client/auth/verify-otp", props);

export const getClientActions = async (props: {
  appId: string;
}): Promise<GlobalAxiosResponse<ActionItem[]>> =>
  api.get(`/client/actions?app_id=${props.appId}`);

export const resentOTP = async (props: {
  email: string;
}): Promise<GlobalAxiosResponse<{ email: string }>> =>
  api.post("/client/auth/resent-otp", props);

export type TemplateAppType = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  is_premium: boolean;
  is_external: boolean;
  external_app_url: string;
  icon?: string;
  image?: string;
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: Record<string, any>;
};

export const getAppTypesForClient = async (): Promise<
  GlobalAxiosResponse<Array<TemplateAppType>>
> => api.get(`/client/apps/types/list`);

export const getTemplatesModelsList = async (
  id: string
): Promise<
  GlobalAxiosResponse<Array<{ id: string; name: string; type: ModalTaskType }>>
> => api.get(`/client/apps/template-ai-models/${id}`);

export interface ActionItem {
  id: string;
  name: string;
  label: string;
  base_coin_price: string;
  coin_tokens: string;
  max_tokens: string;
  type: string;
  is_active: boolean;
  app_id: string;
  super_admin_action_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  superAdminAction: SuperAdminAction;
}

interface SuperAdminAction {
  id: string;
  name: string;
  label: string;
  base_coin_price: string;
  coin_tokens: string;
  max_tokens: string;
  type: string;
  is_active: boolean;
  super_admin_app_type_id: string;
  super_admin_id: string;
  ai_model_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export const createClientApp = async (
  props: CreateApp
): Promise<
  GlobalAxiosResponse<{
    id: string;
    name: string;
    app_type_name: string;
    url: string;
    is_active: boolean;
    client_id: string;
    super_admin_app_type_id: string;
    description?: string;
    theme: object;
    localization: object;
    slug: string;
    logo?: string | null;
    is_paid?: boolean;
    created_at?: Date | string;
    updated_at?: Date | string;
    deleted_at?: Date | string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userData: any;
  }>
> => api.post(`/client/apps`, props);
export const getMagicLink = async (props: {
  email: string;
  type: string;
}): Promise<
  GlobalAxiosResponse<{
    token: string;
  }>
> =>
  api.get(`/client/auth/magic-link-login`, {
    params: {
      email: props.email,
      user_type: props.type,
    },
  });
export const updateClientApp = async (
  props: updateApp
): Promise<
  GlobalAxiosResponse<{
    id: string;
    name: string;
    is_active: boolean;
    app_type_name: string;
    url: string;
    description: string;
    is_paid: boolean;
    logo: string;
    theme: Theme;
    localization: Record<string, unknown>;
    super_admin_app_type_id: string;
    client_id: string;
    created_at: string; // or Date if you parse it
    updated_at: string; // or Date if you parse it
    deleted_at: string | null; // nullable for deleted items
  }>
> => api.put(`/client/apps/${props.id}`, omit(props, ["id"]));

// Gate Open

export const saveBlog = async (props: {
  title: string;
  content: string;
  token: string;
  authId: string;
  status: string;
  metaTitle: string;
  description: string;
  url: string;
  appName: string;
  tagTitle: string[];
  coverImage: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<GlobalAxiosResponse<any>> => {
  return axios.post(
    `https://${props.appName}.${props?.url}/api/blog/create`,
    omit(props, ["token", "appName", "url"]),
    {
      headers: {
        Authorization: `Bearer ${props.token}`,
        "x-app-name": props.appName,
      },
    }
  );
};

export const saveUser = async (props: {
  first_name: string;
  last_name: string;
  email: string;
  id: string;
  avatar: string;
  url: string;

  appName: string;
}): Promise<
  GlobalAxiosResponse<{
    id: string;
    firstName: string;
    lastName: string;
    profilePic: string;
    email: string;
    appId: string;
    createdAt: Date;
    updatedAt: Date;
  }>
> =>
  axios.post(
    `https://${props.appName}.${props?.url}/api/auth/save-user`,
    omit(props, ["appName", "url"]),
    {
      headers: {
        "x-app-name": props.appName,
      },
    }
  );
export const generateBlog = async (props: {
  prompt: string;
  systemPrompt: string;
  /** PM login JWT (previously: per-action gate token) */
  token: string;
  /** optional app scope for model/pricing resolution */
  appId?: string;
}): Promise<GlobalAxiosResponse<string>> =>
  axios.post(
    `${import.meta.env.VITE_PROVIDER_URL}/text-to-text/generate`,
    JSON.stringify({
      prompt: props.prompt,
      system_prompt: props.systemPrompt,
      ...(props.appId ? { app_id: props.appId } : {}),
    }),
    {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${props.token}`,
      },
    }
  );
export const generateImage = async (props: {
  prompt: string;
  systemPrompt: string;
  /** PM login JWT (previously: per-action gate token) */
  token: string;
  /** optional app scope for model/pricing resolution */
  appId?: string;
}): Promise<
  GlobalAxiosResponse<{
    keys: { fileKey: string; publicURL: string }[];
    urls: string[];
  }>
> =>
  axios.post(
    `${import.meta.env.VITE_PROVIDER_URL}/text-to-image/generate`,
    JSON.stringify({
      prompt: props.prompt,
      size: "1792x1024",
      system_prompt: props.systemPrompt,
      ...(props.appId ? { app_id: props.appId } : {}),
    }),
    {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${props.token}`,
      },
    }
  );

export const clientOnboarded = async (): Promise<GlobalAxiosResponse> =>
  api.get(`/client/profile/update-onboarding-status`, {});
